import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import NavigateBeforeSharpIcon from "@mui/icons-material/NavigateBeforeSharp";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { SearchSummary } from "aderant-conflicts-models";
import { getCreatedSearchesRefinedByViewRefiner, getCurrentFocusedHit, getCurrentHitComments } from "state/selectors";
import { searchActions } from "state/actions";
import { RootState } from "MyTypes";
import { MOBILE_VIEWS } from "constants/mobileViews";
import { extractSearchPathInfo } from "utilities/pathUtils";
import { getSourceTypeDisplayValue } from "Functions/strings";

// Constants for strings that might need internationalization
const UI_STRINGS = {
    UNKNOWN: 'Unknown',
    PARTY: 'Party',
    COMMENT: 'Comment',
    COMMENTS: 'Comments',
    REVIEW: 'Review',
    HIT: 'Hit',
    HITS: 'Hits'
} as const;

export const HeaderMobile = (): JSX.Element => {
    const searchSummaries: SearchSummary[] = useSelector(getCreatedSearchesRefinedByViewRefiner, shallowEqual);
    const currentFocusedHit = useSelector(getCurrentFocusedHit);
    const mobileView = useSelector((state: RootState) => state.search.currentSearch.mobileView);
    const currentSearch = useSelector((state: RootState) => state.search.currentSearch.searchVersion);
    
    // Create a memoized selector for hit comments
    const selectedHitResult = mobileView?.selectedHitResult;
    
    // Memoize the durableHitIdentifier object to prevent unnecessary re-renders
    const durableHitIdentifier = useMemo(() => {
        if (selectedHitResult) {
            return {
                requestTermId: selectedHitResult.hitId.requestTermId,
                hitEntityType: selectedHitResult.hit.sourceType,
                hitEntityId: selectedHitResult.hit.sourceData.id
            };
        }
        return undefined;
    }, [
        selectedHitResult?.hitId.requestTermId,
        selectedHitResult?.hit.sourceType,
        selectedHitResult?.hit.sourceData.id
    ]);
    
    const currentHitComments = useSelector((state: RootState) => {
        if (durableHitIdentifier) {
            return getCurrentHitComments(state, durableHitIdentifier);
        }
        return undefined;
    }, shallowEqual);
    
    const dispatch = useDispatch();
    const history = useHistory();

    const pathInfo = extractSearchPathInfo(history.location.pathname);

    const getSearchName = (id: string): string => {
        const search = searchSummaries.find((summary) => summary.id === id);
        return search?.name || "-";
    };

    const getHitName = (): string => {
        // For mobile navigation, use the selected hit from mobile view state
        if (mobileView?.selectedHit?.hitName) {
            return mobileView.selectedHit.hitName;
        }
        // Fallback to desktop focused hit system
        if (currentFocusedHit?.sourceData?.name) {
            return currentFocusedHit.sourceData.name;
        }
        return "-";
    };

    // Helper functions for navigation
    const handleCommentsToHitDetail = () => {
        dispatch(searchActions.setMobileView(MOBILE_VIEWS.HIT_DETAIL));
    };

    const handleHitDetailToCards = () => {
        dispatch(searchActions.setMobileView(MOBILE_VIEWS.CARDS));
        dispatch(searchActions.clearMobileSelectedHit());
    };

    const handleResultsToSearchRequests = () => {
        history.push("/search-requests");
    };

    const handleBack = () => {
        // Check if we're in mobile view navigation
        if (mobileView) {
            if (mobileView.currentView === MOBILE_VIEWS.COMMENTS) {
                // Go back from comments to hit detail
                handleCommentsToHitDetail();
                return;
            } else if (mobileView.currentView === MOBILE_VIEWS.HIT_DETAIL) {
                // Go back from hit detail to cards and clear selected hit
                handleHitDetailToCards();
                return;
            }
        }
        
        // Default behavior: go back to search requests if on results page
        if (pathInfo.isResultsPage) {
            handleResultsToSearchRequests();
        }
    };

    const getHeaderTitle = (): string => {
        // For mobile hit detail view, show the hit name
        if (mobileView?.currentView === MOBILE_VIEWS.HIT_DETAIL && mobileView.selectedHit) {
            return getHitName();
        }
        // For results page, show search name
        if (pathInfo.isResultsPage && pathInfo.searchId) {
            return getSearchName(pathInfo.searchId);
        }
        // Fallback to hit name if we have a focused hit (desktop behavior)
        if (currentFocusedHit) {
            return getHitName();
        }
        return "-";
    };

    // Helper functions for subtitle generation
    const getCommentsSubtitle = (): string => {
        const commentCount = currentHitComments?.length || 0;
        const commentText = commentCount === 1 ? UI_STRINGS.COMMENT : UI_STRINGS.COMMENTS;
        return `${commentCount} ${commentText}`;
    };

    const getHitDetailSubtitle = (): string => {
        if (mobileView?.selectedHitResult?.hit?.sourceType) {
            const hitType = mobileView.selectedHitResult.hit.sourceType;
            return `${getSourceTypeDisplayValue(hitType)}`; // Use the utility function for display value
        }
        return `${UI_STRINGS.UNKNOWN} ${UI_STRINGS.PARTY}`;
    };

    const getCardsSubtitle = (): string => {
        if (currentSearch?.requestTerms) {
            let totalHits = 0;
            for (const term of currentSearch.requestTerms) {
                totalHits += term.hits?.length || 0;
            }
            const hitText = totalHits === 1 ? UI_STRINGS.HIT : UI_STRINGS.HITS;
            return `${UI_STRINGS.REVIEW} ${totalHits} ${hitText}`;
        }
        return "";
    };

    const getHeaderSubtitle = (): string => {
        if (!mobileView) return "";

        switch (mobileView.currentView) {
            case MOBILE_VIEWS.COMMENTS:
                return getCommentsSubtitle();
            case MOBILE_VIEWS.HIT_DETAIL:
                return getHitDetailSubtitle();
            case MOBILE_VIEWS.CARDS:
                return getCardsSubtitle();
            default:
                return "";
        }
    };

    return (
        <div className="mobile-header-hitContainer">
            <IconButton color="primary" size="small" style={{ position: "relative" }} onClick={handleBack}>
                {" "}
                <NavigateBeforeSharpIcon />
            </IconButton>
            <div className="mobile-header-content">
                <div className="mobile-header-hitTitle">{getHeaderTitle()}</div>
                <div className="mobile-header-subtitle">{getHeaderSubtitle()}</div>
            </div>
        </div>
    );
};
